import { useRef, useState } from 'react';
import AuthButton from './auth/AuthButton';
import ManualsButton from './manuals/ManualsButton';
import AIChatCard, { MyManualsCard, LockedCard } from './home/AIChatCard';
import PromptBar from './home/PromptBar';
import PlansGate, { goToPlans } from './home/PlansPage';
import LegalGate from './legal/LegalPage';
import { NAVARCH_THEME } from './theme/navarchTheme';
import ThemeToggle from './theme/ThemeToggle';
import { BRAND, BrandLogo, PAYMENTS_VISIBLE } from './brand';
import { useAuth } from './auth/AuthContext';
import { useMyPlan } from './lib/useMyPlan';
import { supabase } from './lib/supabaseClient';
import { accountLabel } from './lib/authLogic';

// Copyright lockdown (Phase 3.5): these modules answer from licensed manual
// content and are only shown to admin accounts. Hiding is cosmetic — the
// real enforcement is the 403 in api/chat.js / api/chat-purifier.js.
const ADMIN_ONLY_CATEGORIES = ['Main Engine', 'Auxiliaries'];

// SourcesPanel — internal reference context (admin only, never shown to end users)
function SourcesPanel({ sources, accentColor }) {
  const [expanded, setExpanded] = useState(false);
  if (!sources || sources.length === 0) return null;
  return (
    <div
      style={{
        backgroundColor: 'var(--surface-1)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          color: 'var(--text-3)',
          fontFamily: 'var(--sans)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: 2 }}>
            // REFERENCE CONTEXT
          </span>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              backgroundColor: 'var(--surface-3)',
              padding: '2px 7px',
              borderRadius: 5,
            }}
          >
            {sources.length} sections
          </span>
        </div>
        <span style={{ fontSize: 12, color: accentColor }}>{expanded ? '▲ HIDE' : '▼ SHOW'}</span>
      </button>
      {expanded && (
        <div
          style={{
            borderTop: '1px solid var(--border-soft)',
            padding: '16px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>
            Internal reference — visible to admins only.
          </div>
          {sources.map((s, i) => (
            <div
              key={i}
              style={{
                backgroundColor: 'var(--surface-2)',
                border: '1px solid var(--border-soft)',
                borderRadius: 10,
                padding: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10,
                    backgroundColor: 'rgba(34,211,238,0.12)',
                    color: '#0e7490',
                    padding: '2px 8px',
                    borderRadius: 5,
                    fontWeight: 700,
                  }}
                >
                  REF {s.page || '?'}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10,
                    color: 'var(--text-3)',
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                  }}
                >
                  {s.source_type || 'reference'}
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--text-2)',
                  lineHeight: 1.6,
                  fontFamily: 'var(--mono)',
                }}
              >
                {s.preview}...
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PRINT_STYLES = `
@media print {
  body { background: white !important; color: black !important; font-family: Arial, sans-serif; }
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  .print-section { break-inside: avoid; margin-bottom: 16px; }
  .print-header { border-bottom: 2px solid black; padding-bottom: 12px; margin-bottom: 20px; }
  .print-item { border: 1px solid #ccc; border-radius: 4px; padding: 10px 12px; margin-bottom: 8px; display: flex; gap: 12px; align-items: flex-start; }
  .print-checkbox { width: 16px; height: 16px; border: 2px solid #333; flex-shrink: 0; margin-top: 3px; }
  .print-type-badge { font-size: 10px; font-weight: bold; padding: 2px 8px; border: 1px solid #333; border-radius: 3px; margin-bottom: 8px; display: inline-block; }
  .print-obs-text { font-size: 12px; line-height: 1.5; }
  .print-meta { font-size: 10px; color: #666; margin-top: 4px; }
  .print-qnum { font-size: 10px; font-weight: bold; margin-bottom: 4px; }
  .print-footer { border-top: 1px solid #ccc; padding-top: 8px; margin-top: 20px; font-size: 10px; color: #666; text-align: center; }
  @page { margin: 20mm; }
}
@media screen {
  .print-only { display: none; }
}
`;

const DYNAMIC_STYLES = `
:root {
  --bg: #0b1220;
  --surface-1: #101a2e;
  --surface-2: #16213a;
  --surface-3: #1c2a48;
  --border: #233452;
  --border-soft: #1a2740;
  --text: #e6ecf7;
  --text-2: #93a4c3;
  --text-3: #5c6f93;
  --amber: #f59e0b;
  --cyan: #22d3ee;
  --green: #34d399;
  --red: #f87171;
  --yellow: #fbbf24;
  --mono: "JetBrains Mono", "SFMono-Regular", Consolas, monospace;
  --sans: "Inter", -apple-system, "Segoe UI", system-ui, Roboto, sans-serif;
}
@keyframes pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.5); }
  50% { box-shadow: 0 0 0 5px rgba(52,211,153,0); }
}
.app-root {
  min-height: 100vh;
  background: var(--bg);
  background-image:
    radial-gradient(1200px 600px at 70% -10%, rgba(34,211,238,0.07), transparent 60%),
    radial-gradient(900px 500px at 10% 0%, rgba(245,158,11,0.06), transparent 55%);
  background-attachment: fixed;
  color: var(--text);
  font-family: var(--sans);
  font-size: 15px;
  -webkit-font-smoothing: antialiased;
}
.app-header {
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 28px;
  background: rgba(11,18,32,0.85); backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--border-soft);
}
.brand { display: flex; align-items: center; gap: 12px; cursor: pointer; }
.brand-mark {
  width: 36px; height: 36px; border-radius: 10px;
  background: linear-gradient(135deg, var(--surface-3), var(--surface-1));
  border: 1px solid var(--border);
  display: flex; align-items: center; justify-content: center;
}
.brand-name { font-weight: 700; font-size: 17px; letter-spacing: 0.5px; }
.brand-name em { font-style: normal; color: var(--amber); }
.brand-sub { font-family: var(--mono); font-size: 10px; color: var(--text-3); letter-spacing: 1.5px; margin-top: 1px; }
.status-pill {
  display: flex; align-items: center; gap: 8px;
  background: rgba(52,211,153,0.12); border: 1px solid rgba(52,211,153,0.3);
  border-radius: 999px; padding: 5px 14px;
  font-family: var(--mono); font-size: 10.5px; color: var(--green); letter-spacing: 1px;
}
.pulse-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--green); animation: pulse 2s infinite; }

.main-content { max-width: 980px; margin: 0 auto; padding: 36px 24px 80px; }

/* HERO */
.hero-section { text-align: center; padding: 44px 20px 40px; }
.hero-kicker {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--mono); font-size: 10.5px; letter-spacing: 2.5px; color: var(--cyan);
  background: rgba(34,211,238,0.12); border: 1px solid rgba(34,211,238,0.25);
  border-radius: 999px; padding: 6px 16px; margin-bottom: 22px;
}
.hero-title { font-size: clamp(26px, 4.5vw, 40px); font-weight: 800; letter-spacing: -0.5px; line-height: 1.15; margin: 0; }
.hero-title .grad {
  background: linear-gradient(90deg, var(--amber), #fbbf24 45%, var(--cyan));
  -webkit-background-clip: text; background-clip: text; color: transparent;
}
.hero-sub { color: var(--text-2); font-size: 15.5px; max-width: 560px; margin: 16px auto 0; line-height: 1.65; }
.hero-meta { display: flex; justify-content: center; gap: 28px; margin-top: 30px; flex-wrap: wrap; }
.hero-meta .num { font-size: 22px; font-weight: 800; text-align: center; }
.hero-meta .cap { font-family: var(--mono); font-size: 10px; color: var(--text-3); letter-spacing: 1.5px; margin-top: 3px; text-align: center; }

.section-label {
  font-family: var(--mono); font-size: 11px; letter-spacing: 2.5px; color: var(--text-3);
  display: flex; align-items: center; gap: 12px; margin: 36px 0 18px;
}
.section-label::after { content: ""; flex: 1; height: 1px; background: var(--border-soft); }

/* CATEGORY CARDS */
.category-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.cat-card {
  position: relative;
  background: linear-gradient(180deg, var(--surface-2), var(--surface-1));
  border: 1px solid var(--border); border-radius: 14px;
  padding: 26px 22px; cursor: pointer;
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
  overflow: hidden;
}
.cat-card::before {
  content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: var(--card-accent); opacity: 0.85;
}
.cat-card:hover { transform: translateY(-4px); border-color: var(--card-accent); box-shadow: 0 12px 32px -12px rgba(0,0,0,0.6); }
.cat-icon {
  width: 46px; height: 46px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
  background: var(--card-accent-soft);
  border: 1px solid var(--card-accent-border);
}
.cat-title { font-weight: 700; font-size: 16px; margin-bottom: 6px; }
.cat-desc { color: var(--text-2); font-size: 13px; line-height: 1.55; }
.cat-badge {
  position: absolute; top: 16px; right: 16px;
  font-family: var(--mono); font-size: 9px; letter-spacing: 1px; font-weight: 700;
  color: var(--green); background: rgba(52,211,153,0.12);
  border: 1px solid rgba(52,211,153,0.3); border-radius: 5px; padding: 3px 8px;
}
.cat-foot { margin-top: 18px; font-family: var(--mono); font-size: 10.5px; color: var(--text-3); display: flex; align-items: center; gap: 6px; }
.cat-foot .arrow { color: var(--card-accent); transition: transform 0.2s; }
.cat-card:hover .arrow { transform: translateX(4px); }

/* STEPPER */
.stepper { display: flex; align-items: center; margin-bottom: 32px; flex-wrap: wrap; }
.step { display: flex; align-items: center; gap: 8px; }
.step .dot {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; font-family: var(--mono);
  background: var(--surface-3); color: var(--text-3); border: 1px solid var(--border);
}
.step.done .dot { background: var(--card-accent-soft); color: var(--card-accent); border-color: var(--card-accent-border); }
.step.current .dot { background: var(--card-accent); color: #0b1220; border-color: var(--card-accent); }
.step .name { font-size: 12.5px; color: var(--text-3); }
.step.done .name { color: var(--text-2); cursor: pointer; }
.step.done .name:hover { color: var(--text); }
.step.current .name { color: var(--text); font-weight: 600; }
.step-line { width: 36px; height: 1px; background: var(--border); margin: 0 12px; }

/* SELECT CARDS (manufacturer / type / model / rank / dept) */
.select-card {
  background: linear-gradient(180deg, var(--surface-2), var(--surface-1));
  border: 1px solid var(--border); border-radius: 14px;
  padding: 24px; cursor: pointer;
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
}
.select-card:hover { transform: translateY(-2px); border-color: var(--card-accent); box-shadow: 0 8px 24px -10px rgba(0,0,0,0.6); }
.sc-title { font-weight: 700; font-size: 16px; color: var(--card-accent); }
.sc-sub { color: var(--text-3); font-size: 12px; margin-top: 6px; }

/* EQUIPMENT CONTEXT BAR */
.equip-bar {
  display: flex; align-items: center; gap: 14px;
  background: var(--surface-1); border: 1px solid var(--border); border-radius: 14px;
  padding: 14px 18px; margin-bottom: 20px;
}
.equip-icon {
  width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0;
  background: var(--card-accent-soft); border: 1px solid var(--card-accent-border);
  display: flex; align-items: center; justify-content: center;
}
.equip-name { font-weight: 700; font-size: 14.5px; }
.equip-path { font-family: var(--mono); font-size: 10.5px; color: var(--text-3); letter-spacing: 0.5px; margin-top: 2px; }
.equip-change {
  margin-left: auto; background: transparent; border: 1px solid var(--border); color: var(--text-2);
  border-radius: 8px; padding: 7px 14px; font-size: 12px; cursor: pointer; font-family: var(--sans);
  flex-shrink: 0;
}
.equip-change:hover { border-color: var(--card-accent); color: var(--card-accent); }

/* PANELS */
.panel {
  background: linear-gradient(180deg, var(--surface-2), var(--surface-1));
  border: 1px solid var(--border); border-radius: 14px;
  padding: 24px; margin-bottom: 16px;
}
.panel-label {
  font-family: var(--mono); font-size: 10.5px; letter-spacing: 2px; color: var(--text-3);
  margin-bottom: 14px; display: flex; align-items: center; gap: 8px;
}
.panel-tick { width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0; }
.panel-assessment {
  border-left: 3px solid var(--card-accent);
  background: linear-gradient(90deg, var(--card-accent-soft), var(--surface-1) 50%);
}

/* INPUT */
.input-field {
  width: 100%; min-height: 96px; background: rgba(8,12,22,0.5);
  border: 1px solid var(--border); border-radius: 10px;
  color: var(--text); font-size: 14px; font-family: var(--sans);
  padding: 14px 16px; resize: vertical; outline: none; transition: border-color 0.15s;
  line-height: 1.6;
}
.input-field:focus { border-color: var(--card-accent); }
.text-input {
  background: rgba(8,12,22,0.5); border: 1px solid var(--border); border-radius: 10px;
  padding: 13px 16px; color: var(--text); font-size: 13.5px; outline: none; font-family: var(--sans);
}
.text-input:focus { border-color: var(--card-accent); }
.example-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.chip {
  background: transparent; border: 1px solid var(--border); color: var(--text-2);
  border-radius: 999px; padding: 6px 14px; font-size: 12px; cursor: pointer;
  transition: all 0.15s; font-family: var(--sans);
}
.chip:hover { border-color: var(--card-accent); color: var(--card-accent); background: var(--card-accent-soft); }
.btn {
  border: none; border-radius: 10px; padding: 13px 26px;
  font-weight: 700; font-size: 13.5px; cursor: pointer; font-family: var(--sans);
  letter-spacing: 0.3px; transition: filter 0.15s, transform 0.1s;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
}
.btn:active { transform: scale(0.98); }
.btn:hover:not(:disabled) { filter: brightness(1.08); }
.btn:disabled { cursor: not-allowed; opacity: 0.6; }
.btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-2); font-weight: 500; }
.btn-ghost:hover { border-color: var(--text-2); color: var(--text); filter: none; }

/* RESULT */
.sev-row { display: flex; gap: 14px; align-items: flex-start; padding: 12px 0; border-bottom: 1px solid var(--border-soft); }
.sev-row:last-child { border-bottom: none; padding-bottom: 0; }
.sev-row:first-of-type { padding-top: 0; }
.sev-chip {
  font-family: var(--mono); font-size: 9.5px; font-weight: 700; letter-spacing: 1px;
  border-radius: 6px; padding: 4px 10px; flex-shrink: 0; margin-top: 1px;
}
.check-item { display: flex; gap: 12px; padding: 9px 0; font-size: 13.5px; line-height: 1.6; align-items: baseline; }
.check-item .mark { color: var(--card-accent); font-family: var(--mono); flex-shrink: 0; }
.timeline { padding-left: 8px; }
.t-step { display: flex; gap: 18px; position: relative; padding-bottom: 24px; }
.t-step:last-child { padding-bottom: 0; }
.t-step::before {
  content: ""; position: absolute; left: 15px; top: 34px; bottom: 2px; width: 2px;
  background: linear-gradient(180deg, var(--border), transparent);
}
.t-step:last-child::before { display: none; }
.t-num {
  width: 32px; height: 32px; border-radius: 10px; flex-shrink: 0;
  background: var(--card-accent-soft); border: 1px solid var(--card-accent-border);
  color: var(--card-accent); font-weight: 800; font-size: 13px; font-family: var(--mono);
  display: flex; align-items: center; justify-content: center;
}
.t-title { font-weight: 700; font-size: 14px; margin-bottom: 4px; padding-top: 5px; }
.t-desc { font-size: 13px; color: var(--text-2); line-height: 1.65; }
.disclaimer {
  text-align: center; font-size: 11.5px; color: var(--text-3); padding: 18px 0 0;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}

/* MOCK INSPECTION */
.mock-score { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; flex-wrap: wrap; gap: 10px; }
.score-pill { font-family: var(--mono); font-size: 11px; font-weight: 700; border-radius: 8px; padding: 5px 12px; }
.sp-pass { background: rgba(52,211,153,0.12); color: var(--green); }
.sp-part { background: rgba(251,191,36,0.12); color: var(--yellow); }
.sp-fail { background: rgba(248,113,113,0.12); color: var(--red); }
.sp-skip { background: var(--surface-3); color: var(--text-3); }
.progress-track { height: 5px; background: var(--surface-3); border-radius: 99px; margin-bottom: 24px; overflow: hidden; }
.progress-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, var(--green), #6ee7b7); transition: width 0.3s; }
.q-alert {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(251,191,36,0.1); border: 1px solid rgba(251,191,36,0.3);
  color: var(--yellow); font-size: 12px; border-radius: 8px; padding: 7px 14px; margin-bottom: 14px;
}
.msg { display: flex; gap: 12px; margin-bottom: 18px; }
.msg.user { flex-direction: row-reverse; }
.avatar {
  width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 15px;
  border: 1px solid var(--border);
}
.avatar.insp { background: rgba(34,211,238,0.1); border-color: rgba(34,211,238,0.3); }
.avatar.you { background: rgba(52,211,153,0.12); border-color: rgba(52,211,153,0.3); }
.bubble { max-width: 78%; }
.bubble .who { font-family: var(--mono); font-size: 9.5px; letter-spacing: 1.5px; color: var(--text-3); margin-bottom: 5px; }
.msg.user .who { text-align: right; }
.bubble .body { padding: 13px 16px; border-radius: 12px; font-size: 13.5px; line-height: 1.65; }
.msg.insp .body { background: var(--surface-3); border: 1px solid var(--border); border-top-left-radius: 4px; }
.msg.user .body { background: rgba(52,211,153,0.1); border: 1px solid rgba(52,211,153,0.25); border-top-right-radius: 4px; }
.inspector-input-row { display: flex; gap: 10px; margin-top: 6px; }

/* CHECKLIST */
.checklist-filter { display: flex; gap: 8px; margin-bottom: 22px; flex-wrap: wrap; }
.filter-btn {
  background: transparent; border: 1px solid var(--border); color: var(--text-2);
  border-radius: 999px; padding: 7px 18px; font-size: 12.5px; cursor: pointer;
  font-family: var(--sans); font-weight: 600;
}
.filter-btn.active { background: var(--green); border-color: var(--green); color: #0b1220; }
.cl-type {
  font-family: var(--mono); font-size: 10px; font-weight: 700; letter-spacing: 1.5px;
  border-radius: 6px; padding: 4px 12px;
}
.cl-item {
  display: flex; gap: 14px; align-items: flex-start;
  background: var(--surface-1); border: 1px solid var(--border-soft); border-radius: 12px;
  padding: 15px 18px; margin-bottom: 8px; transition: border-color 0.15s; cursor: pointer;
}
.cl-item:hover { border-color: var(--border); }
.cl-check {
  width: 20px; height: 20px; border-radius: 6px; border: 1.5px solid var(--text-3);
  flex-shrink: 0; margin-top: 2px; transition: all 0.15s; position: relative;
}
.cl-item.checked .cl-check { background: var(--green); border-color: var(--green); }
.cl-item.checked .cl-check::after {
  content: "✓"; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  color: #0b1220; font-size: 13px; font-weight: 800;
}
.cl-item.checked .cl-text { color: var(--text-3); text-decoration: line-through; }
.cl-qn { font-family: var(--mono); font-size: 10.5px; color: var(--cyan); margin-bottom: 4px; }
.cl-text { font-size: 13.5px; line-height: 1.6; }

.model-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.rank-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.dept-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.chat-input-row { display: flex; gap: 10px; }

/* MOBILE RESPONSIVE */
@media (max-width: 600px) {
  .category-grid { grid-template-columns: 1fr; gap: 12px; }
  .model-grid { grid-template-columns: repeat(2, 1fr); }
  .rank-grid { grid-template-columns: repeat(2, 1fr); }
  .dept-grid { grid-template-columns: repeat(2, 1fr); }
  .hero-section { padding: 24px 12px 24px; }
  .main-content { padding: 20px 16px 60px; }
  .app-header { padding: 12px 16px; }
  .chat-input-row { flex-direction: column; }
  .chat-input-row .btn { width: 100%; }
  .inspector-input-row { flex-direction: column; }
  .inspector-input-row .btn { width: 100%; }
  .stepper .step .name { display: none; }
  .stepper .step.current .name { display: inline; }
  .step-line { width: 16px; margin: 0 6px; }
  .checklist-filter { flex-wrap: wrap; }
  .bubble { max-width: 90%; }
  .example-tags { display: none; }
  .hero-meta { gap: 18px; }
  .equip-bar { flex-wrap: wrap; }
}
@media (max-width: 400px) {
  .model-grid { grid-template-columns: 1fr; }
  .rank-grid { grid-template-columns: 1fr; }
}
`;

// Inline SVG icons (Feather-style, no dependency) — consistent across all platforms
function SvgIcon({ paths, size = 24, color = 'currentColor' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths}
    </svg>
  );
}
const ICON_PATHS = {
  anchor: (
    <>
      <circle cx="12" cy="5" r="3" />
      <line x1="12" y1="22" x2="12" y2="8" />
      <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  drop: <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />,
  clipboard: (
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </>
  ),
};

const SEV_STYLES = {
  CRITICAL: {
    backgroundColor: 'rgba(248,113,113,0.2)',
    color: '#b91c1c',
    border: '1px solid rgba(248,113,113,0.5)',
  },
  HIGH: {
    backgroundColor: 'rgba(248,113,113,0.12)',
    color: '#dc2626',
    border: '1px solid rgba(248,113,113,0.35)',
  },
  MED: {
    backgroundColor: 'rgba(251,191,36,0.12)',
    color: '#a16207',
    border: '1px solid rgba(251,191,36,0.35)',
  },
  LOW: {
    backgroundColor: 'rgba(52,211,153,0.12)',
    color: '#047857',
    border: '1px solid rgba(52,211,153,0.35)',
  },
};

// Per-category accent CSS vars — drive card borders, chips, buttons, focus rings
function accentVars(color) {
  return {
    '--card-accent': color,
    '--card-accent-soft': `${color}1f`,
    '--card-accent-border': `${color}59`,
  };
}

const EQUIPMENT_TREE = {
  'Main Engine': {
    icon: 'gear',
    color: '#b45309',
    description: 'MAN B&W ME-GI guided troubleshooting, alarm diagnosis & performance deviations.',
    footer: 'G70ME-C10.5-GI',
    manufacturers: {
      'MAN B&W': {
        types: {
          'ME-GI': {
            models: {
              'G70ME-C10.5-GI': {
                id: 'main-engine_man-bw_me-gi_g70me-c10.5-gi',
                mode: 'qa',
                examples: [
                  'High exhaust temperature cylinder 4',
                  'Low scavenge air pressure alarm',
                  'Turbocharger surging at high load',
                  'Abnormal lube oil consumption',
                ],
                placeholder:
                  'e.g. "High exhaust temp on cyl. 4, +48°C deviation from mean at 85% load"',
              },
            },
          },
        },
      },
    },
  },
  Auxiliaries: {
    icon: 'drop',
    color: '#0e7490',
    description: 'Alfa Laval purifier alarm-code lookup and targeted diagnosis.',
    footer: 'P615 · P626 · S938 · S949',
    manufacturers: {
      'Alfa Laval': {
        types: {
          Purifier: {
            models: {
              'P 615': {
                id: 'purifier_alfa-laval_p615',
                mode: 'qa',
                examples: [
                  'Heavy vibration on bowl',
                  'Water carryover to clean oil tank',
                  'Bowl not opening at discharge',
                  'High back pressure alarm',
                ],
                placeholder:
                  'e.g. "Purifier vibrating heavily, bowl speed dropping, water in outlet"',
              },
              'P 626': {
                id: 'purifier_alfa-laval_p626',
                mode: 'qa',
                examples: [
                  'Heavy vibration on bowl',
                  'Water carryover to clean oil tank',
                  'Bowl not opening at discharge',
                  'High back pressure alarm',
                ],
                placeholder:
                  'e.g. "Purifier vibrating heavily, bowl speed dropping, water in outlet"',
              },
              'S 938': {
                id: 'purifier_alfa-laval_s938',
                mode: 'qa',
                examples: [
                  'Heavy vibration on bowl',
                  'Water carryover to clean oil tank',
                  'Bowl not opening at discharge',
                  'High back pressure alarm',
                ],
                placeholder:
                  'e.g. "Purifier vibrating heavily, bowl speed dropping, water in outlet"',
              },
              'S 949': {
                id: 'purifier_alfa-laval_s949',
                mode: 'qa',
                examples: [
                  'Heavy vibration on bowl',
                  'Water carryover to clean oil tank',
                  'Bowl not opening at discharge',
                  'High back pressure alarm',
                ],
                placeholder:
                  'e.g. "Purifier vibrating heavily, bowl speed dropping, water in outlet"',
              },
            },
          },
        },
      },
    },
  },
  Inspection: {
    icon: 'clipboard',
    color: '#047857',
    description: 'SIRE 2.0 mock inspections by rank, Q&A assistant and printable checklists.',
    footer: 'SIRE 2.0 VETTING',
    manufacturers: {
      Vetting: {
        types: {
          'SIRE 2.0': {
            models: {
              'Mock Inspection': {
                id: 'sire2-mock',
                mode: 'mock',
                examples: [],
                placeholder: 'Select your rank to start mock inspection',
              },
              'Q&A Assistant': {
                id: 'sire2',
                mode: 'qa',
                examples: [
                  'UKC policy and voyage planning',
                  'Enclosed space entry procedures',
                  'Cargo pump emergency stop',
                  'Officer rest hours compliance',
                ],
                placeholder: 'e.g. "Inspector asking about UKC policy and voyage planning process"',
              },
              'Pre-Inspection Checklist': {
                id: 'sire2-checklist',
                mode: 'checklist',
                examples: [],
                placeholder: '',
              },
            },
          },
        },
      },
    },
  },
};

const RANKS = [
  'Master',
  'Chief Officer',
  'Senior Deck Officers',
  'Deck Officers',
  'Navigation Officers',
  'SSO',
  'Chief Engineer',
  'Engineer Officers',
  'Senior Engineer Officers',
  'Junior Engineer Officers',
  'All Officers',
  'Ratings',
  'Deck Ratings',
  'Engine Ratings',
  'Galley Staff',
];

function buildPrompt(equipmentId, problem, chatHistory) {
  const systemPrompts = {
    'main-engine_man-bw_me-gi_g70me-c10.5-gi': `You are an expert marine engineer specializing in MAN B&W ME-GI two-stroke dual fuel engines (G70ME-C10.5-GI). Provide structured troubleshooting guidance. Never quote technical documentation verbatim. Always respond in valid JSON with keys: assessment, causes (array of {severity, description}), checks (array of strings), steps (array of {number, title, description}).`,
    'purifier_alfa-laval_p615': `You are an expert marine engineer specializing in Alfa Laval P 615 centrifugal purifiers. Provide structured troubleshooting guidance. Never quote technical documentation verbatim. Always respond in valid JSON with keys: assessment, causes (array of {severity, description}), checks (array of strings), steps (array of {number, title, description}).`,
    'purifier_alfa-laval_p626': `You are an expert marine engineer specializing in Alfa Laval P 626 centrifugal purifiers. Provide structured troubleshooting guidance. Never quote technical documentation verbatim. Always respond in valid JSON with keys: assessment, causes (array of {severity, description}), checks (array of strings), steps (array of {number, title, description}).`,
    'purifier_alfa-laval_s938': `You are an expert marine engineer specializing in Alfa Laval S 938 centrifugal purifiers. Provide structured troubleshooting guidance. Never quote technical documentation verbatim. Always respond in valid JSON with keys: assessment, causes (array of {severity, description}), checks (array of strings), steps (array of {number, title, description}).`,
    'purifier_alfa-laval_s949': `You are an expert marine engineer specializing in Alfa Laval S 949 centrifugal purifiers. Provide structured troubleshooting guidance. Never quote technical documentation verbatim. Always respond in valid JSON with keys: assessment, causes (array of {severity, description}), checks (array of strings), steps (array of {number, title, description}).`,
    sire2: `You are an expert in SIRE 2.0 vetting inspections for tankers. Provide structured guidance. Always respond in valid JSON with keys: assessment, causes (array of {severity, description}), checks (array of strings), steps (array of {number, title, description}).`,
  };
  const system =
    systemPrompts[equipmentId] || systemPrompts['main-engine_man-bw_me-gi_g70me-c10.5-gi'];
  const historyMessages = chatHistory
    .map(h => [
      { role: 'user', content: h.question },
      { role: 'assistant', content: JSON.stringify(h.result) },
    ])
    .flat();
  const messages = [...historyMessages, { role: 'user', content: `Problem/Question: ${problem}` }];
  return { system, messages };
}

export default function App() {
  const { user } = useAuth();
  const { isAdmin } = useMyPlan();
  const [category, setCategory] = useState(null);
  const [manufacturer, setManufacturer] = useState(null);
  const [engineType, setEngineType] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [problem, setProblem] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customAnswer, setCustomAnswer] = useState('');
  const [sources, setSources] = useState([]); // reference context for transparency
  const [mockRank, setMockRank] = useState(null);
  const [mockQuestions, setMockQuestions] = useState([]);
  const [mockIndex, setMockIndex] = useState(0);
  const [mockScore, setMockScore] = useState({ pass: 0, partial: 0, fail: 0, skipped: 0 });
  const [mockLoadingQ, setMockLoadingQ] = useState(false);
  const [mockError, setMockError] = useState(null);
  const [showExpects, setShowExpects] = useState(false);
  const [inspectorMessages, setInspectorMessages] = useState([]);
  const [inspectorInput, setInspectorInput] = useState('');
  const [inspectorVerdict, setInspectorVerdict] = useState(null);
  const [inspectorLoading, setInspectorLoading] = useState(false);
  const [inspectorStarted, setInspectorStarted] = useState(false);
  const [inspectorError, setInspectorError] = useState(null);
  // The payload of the in-flight/failed inspector round, for ↻ Retry.
  const [lastInspectorRequest, setLastInspectorRequest] = useState(null);
  // Monotonic id per inspector round-trip: bumping it (Skip while the
  // inspector is "typing", moving to the next question) turns any in-flight
  // response into a clean no-op instead of landing on the wrong question.
  const inspectorSeqRef = useRef(0);

  // Checklist state
  const [checklistDept, setChecklistDept] = useState(null);
  const [checklistType, setChecklistType] = useState('ALL');
  const [checklistData, setChecklistData] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checkedItems, setCheckedItems] = useState({}); // on-screen tick state

  function printChecklist() {
    window.print();
  }

  const categoryData = category ? EQUIPMENT_TREE[category] : null;
  const manufacturerData =
    category && manufacturer ? EQUIPMENT_TREE[category].manufacturers[manufacturer] : null;
  const engineTypeData =
    category && manufacturer && engineType
      ? EQUIPMENT_TREE[category].manufacturers[manufacturer].types[engineType]
      : null;

  function resetNavigation() {
    setCategory(null);
    setManufacturer(null);
    setEngineType(null);
    setSelectedModel(null);
    setProblem('');
    setResult(null);
    setError(null);
    setChatHistory([]);
    setShowCustomInput(false);
    setCustomAnswer('');
    setMockRank(null);
    setMockError(null);
    setMockQuestions([]);
    setMockIndex(0);
    setMockScore({ pass: 0, partial: 0, fail: 0, skipped: 0 });
    setInspectorMessages([]);
    setInspectorVerdict(null);
    setInspectorStarted(false);
    setInspectorError(null);
    setLastInspectorRequest(null);
    setShowExpects(false);
    setChecklistDept(null);
    setChecklistType('ALL');
    setChecklistData(null);
    setCheckedItems({});
  }

  function selectCategory(cat) {
    setCategory(cat);
    setManufacturer(null);
    setEngineType(null);
    setSelectedModel(null);
    setResult(null);
    setChatHistory([]);
  }
  function selectManufacturer(mfr) {
    setManufacturer(mfr);
    setEngineType(null);
    setSelectedModel(null);
    setResult(null);
    setChatHistory([]);
  }
  function selectEngineType(type) {
    setEngineType(type);
    setSelectedModel(null);
    setResult(null);
    setChatHistory([]);
  }
  function selectModel(modelName, modelData) {
    setSelectedModel({ name: modelName, ...modelData });
    setResult(null);
    setChatHistory([]);
    setShowCustomInput(false);
    setCustomAnswer('');
    setMockRank(null);
    setMockError(null);
    setMockQuestions([]);
    setMockIndex(0);
    setMockScore({ pass: 0, partial: 0, fail: 0, skipped: 0 });
    setInspectorMessages([]);
    setInspectorVerdict(null);
    setInspectorStarted(false);
    setInspectorError(null);
    setLastInspectorRequest(null);
    setShowExpects(false);
  }

  async function startMockInspection(rank) {
    setMockLoadingQ(true);
    setMockRank(rank);
    setMockError(null);
    setMockQuestions([]);
    setMockIndex(0);
    setMockScore({ pass: 0, partial: 0, fail: 0, skipped: 0 });
    setInspectorMessages([]);
    setInspectorVerdict(null);
    setInspectorStarted(false);
    setInspectorError(null);
    setLastInspectorRequest(null);
    setShowExpects(false);
    try {
      const res = await fetch('/api/sire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_questions', rank }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
      const questions = Array.isArray(data.questions) ? data.questions : [];
      // The server pre-orders questions by relevance (the ordering signal is
      // private and stays server-side): top six as-is + four random others.
      const selected = [
        ...questions.slice(0, 6),
        ...questions
          .slice(6)
          .sort(() => Math.random() - 0.5)
          .slice(0, 4),
      ].slice(0, 10);
      setMockQuestions(selected);
      if (selected.length === 0) {
        setMockError(`No SIRE 2.0 questions are available for "${rank}" yet.`);
      }
    } catch (e) {
      console.error(e);
      setMockError("Couldn't load questions. Please check your connection and try again.");
    } finally {
      setMockLoadingQ(false);
    }
  }

  // Shared inspector round-trip. Any failure — network error, HTTP error, or
  // a 200 with an empty/absent message — surfaces a retryable error message
  // instead of leaving the UI on an endless "typing…" state.
  async function requestInspector(apiMessages, { scoreVerdict }) {
    const currentQ = mockQuestions[mockIndex];
    const seq = ++inspectorSeqRef.current;
    setInspectorLoading(true);
    setInspectorError(null);
    setLastInspectorRequest({ apiMessages, scoreVerdict });
    try {
      const res = await fetch('/api/sire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'inspector_chat',
          question_number: currentQ.question_number,
          messages: apiMessages,
        }),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        // empty or non-JSON body — treated as a failure below
      }
      if (inspectorSeqRef.current !== seq) return; // superseded (e.g. Skip) — drop silently
      if (!res.ok || !data?.message) {
        throw new Error(data?.error || `Inspector request failed (${res.status})`);
      }
      setInspectorMessages(prev => [
        ...prev,
        { role: 'inspector', content: data.message, followups: data.followup_questions },
      ]);
      if (data.verdict) {
        setInspectorVerdict({ verdict: data.verdict, reason: data.verdict_reason });
        if (scoreVerdict) {
          const key =
            data.verdict === 'PASS' ? 'pass' : data.verdict === 'FAIL' ? 'fail' : 'partial';
          setMockScore(prev => ({ ...prev, [key]: prev[key] + 1 }));
        }
      }
    } catch (e) {
      if (inspectorSeqRef.current !== seq) return; // superseded — no orphan error
      console.error(e);
      setInspectorError('The inspector is briefly unavailable — please retry.');
    } finally {
      if (inspectorSeqRef.current === seq) setInspectorLoading(false);
    }
  }

  async function startInspectorChat() {
    setInspectorMessages([]);
    setInspectorVerdict(null);
    setInspectorInput('');
    setShowExpects(false);
    setInspectorStarted(true);
    await requestInspector([{ role: 'user', content: 'Start the inspection for this question.' }], {
      scoreVerdict: false,
    });
  }

  async function sendInspectorReply() {
    if (!inspectorInput.trim() || inspectorLoading) return;
    const apiMessages = [
      ...inspectorMessages.map(m => ({
        role: m.role === 'inspector' ? 'assistant' : 'user',
        content: m.content,
      })),
      { role: 'user', content: inspectorInput },
    ];
    setInspectorMessages(prev => [...prev, { role: 'user', content: inspectorInput }]);
    setInspectorInput('');
    await requestInspector(apiMessages, { scoreVerdict: true });
  }

  // ↻ Retry resends the exact payload of the failed round (the user's last
  // message is already in the thread, so nothing needs retyping).
  function retryInspector() {
    if (!lastInspectorRequest || inspectorLoading) return;
    requestInspector(lastInspectorRequest.apiMessages, {
      scoreVerdict: lastInspectorRequest.scoreVerdict,
    });
  }

  function nextMockQuestion() {
    inspectorSeqRef.current++; // any in-flight inspector reply is now stale
    setMockIndex(prev => prev + 1);
    setInspectorMessages([]);
    setInspectorVerdict(null);
    setInspectorInput('');
    setInspectorStarted(false);
    setInspectorError(null);
    setLastInspectorRequest(null);
    setInspectorLoading(false);
    setShowExpects(false);
  }

  // Skip → advances immediately with NO AI call (zero cost, zero quota).
  // The skipped question consumes its slot (the mock stays at its fixed
  // size — no replacement question) and counts in the neutral SKIPPED
  // bucket, never as PASS/PARTIAL/FAIL. Safe mid-"typing": the seq bump
  // makes any pending inspector reply a silent no-op.
  function skipMockQuestion() {
    setMockScore(prev => ({ ...prev, skipped: prev.skipped + 1 }));
    nextMockQuestion();
  }

  async function loadChecklist(dept, type) {
    setChecklistLoading(true);
    setChecklistData(null);
    setCheckedItems({});
    try {
      const res = await fetch('/api/sire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_checklist', department: dept, obs_type: type }),
      });
      const data = await res.json();
      setChecklistData(data.checklist);
    } catch (e) {
      console.error(e);
    } finally {
      setChecklistLoading(false);
    }
  }

  // Called when engineer picks a clarify option or submits custom answer
  async function analyzeWithAnswer(answer) {
    setShowCustomInput(false);
    setCustomAnswer('');
    setProblem('');
    await runAnalysis(answer);
  }

  async function analyze() {
    if (!problem.trim() || !selectedModel) return;
    await runAnalysis(problem);
  }

  async function runAnalysis(question) {
    setLoading(true);
    setError(null);
    setSources([]);
    const endpoint = selectedModel.id.startsWith('purifier_') ? '/api/chat-purifier' : '/api/chat';
    const { system, messages } = buildPrompt(selectedModel.id, question, chatHistory);
    try {
      // These endpoints are admin-locked server-side; send the JWT.
      let authToken = null;
      if (supabase) {
        const { data: sessionData } = await supabase.auth.getSession();
        authToken = sessionData?.session?.access_token || null;
      }
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({ system, messages, equipment: selectedModel.id }),
      });
      const data = await res.json();
      // Capture sources if present
      if (data._sources && data._sources.length > 0) {
        setSources(data._sources);
      }
      const text = data.content?.find(b => b.type === 'text')?.text || '';
      if (!text) {
        setError(`API Error: ${JSON.stringify(data).slice(0, 200)}`);
        return;
      }
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        setError(`No JSON in: ${text.slice(0, 300)}`);
        return;
      }
      const parsed = JSON.parse(jsonMatch[0].trim());
      setChatHistory(prev => [...prev, { question, result: parsed }]);
      setResult(parsed);
      setProblem('');
      setShowCustomInput(false);
      setCustomAnswer('');
    } catch (e) {
      setError(`Error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  const accentColor = categoryData?.color || '#3b6fe0';
  const G = '#047857';
  const accentStyle = accentVars(accentColor);

  // Wizard stepper — replaces the old breadcrumb
  function renderStepper() {
    const modeLabel = selectedModel
      ? selectedModel.mode === 'mock'
        ? 'Inspection'
        : selectedModel.mode === 'checklist'
          ? 'Checklist'
          : 'Diagnosis'
      : null;
    const steps = [
      { label: category || 'Category', done: !!category, onClick: resetNavigation },
      {
        label: manufacturer || 'Manufacturer',
        done: !!manufacturer,
        onClick: () => selectCategory(category),
      },
      {
        label: engineType || 'Type',
        done: !!engineType,
        onClick: () => selectManufacturer(manufacturer),
      },
      {
        label: selectedModel?.name || 'Model',
        done: !!selectedModel,
        onClick: () => selectEngineType(engineType),
      },
    ];
    const currentIdx = steps.findIndex(s => !s.done);
    return (
      <div className="stepper" style={accentStyle}>
        {steps.map((s, i) => {
          const isCurrent = i === currentIdx;
          if (!s.done && !isCurrent) return null;
          return (
            <span key={i} style={{ display: 'flex', alignItems: 'center' }}>
              {i > 0 && <span className="step-line" />}
              <span className={`step ${s.done ? 'done' : 'current'}`}>
                <span className="dot">{s.done ? '✓' : i + 1}</span>
                <span className="name" onClick={s.done ? s.onClick : undefined}>
                  {s.label}
                </span>
              </span>
            </span>
          );
        })}
        {selectedModel && (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span className="step-line" />
            <span className="step current">
              <span className="dot">5</span>
              <span className="name">{modeLabel}</span>
            </span>
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="app-root">
      <style>{PRINT_STYLES}</style>
      <style>{DYNAMIC_STYLES}</style>
      {/* NavarchAI theme system (light default + dark) — injected AFTER the
          legacy styles so its rules win the cascade. Purely visual. */}
      <style>{NAVARCH_THEME}</style>
      <PlansGate />
      <LegalGate />
      <div className="app-shell">
        {/* HEADER */}
        <header className="app-header no-print">
          <div className="brand" onClick={resetNavigation}>
            <div className="brand-mark">
              {/* Colors come from theme tokens (--logo-stroke / --logo-star)
                  so the mark reads correctly on the navy tile in both themes. */}
              <BrandLogo size={22} />
            </div>
            <div>
              <div className="brand-name">
                {BRAND.nameStart}
                <em>{BRAND.nameAccent}</em>
              </div>
              <div className="brand-sub">{BRAND.tagline.toUpperCase()}</div>
            </div>
          </div>
          <div className="header-actions">
            <div className="status-pill">
              <span className="pulse-dot" /> AI ONLINE
            </div>
            <ThemeToggle />
            <ManualsButton />
            {PAYMENTS_VISIBLE && (
              <button className="pill-primary" onClick={goToPlans} aria-label="Upgrade">
                <span aria-hidden="true">✦</span>
                <span className="pill-label">Upgrade</span>
              </button>
            )}
            <AuthButton />
          </div>
        </header>
        <div className="main-content">
          {category && renderStepper()}

          {/* HERO — only on home */}
          {!category && (
            <div className="hero-section">
              <div className="hero-kicker">⚡ AI-POWERED MARITIME EXPERTISE</div>
              <h1 className="hero-title">
                {user ? (
                  <>
                    <span className="hero-name">Hi {accountLabel(user)},</span> ready for
                    <br />
                    smooth sailing?
                  </>
                ) : (
                  <>
                    Troubleshooting & inspection prep,
                    <br />
                    <span className="grad">built for everyone onboard.</span>
                  </>
                )}
              </h1>
              <p className="hero-sub">
                Organized answers for the problems and paperwork of life at sea.
              </p>
            </div>
          )}

          {!category && (
            <>
              <div className="section-label">SELECT CATEGORY</div>
              <div className="category-grid">
                {Object.entries(EQUIPMENT_TREE).map(([cat, data]) => {
                  const adminOnly = ADMIN_ONLY_CATEGORIES.includes(cat);
                  // Licensed-content modules: admins only (server enforces too).
                  if (adminOnly && !isAdmin) return null;
                  return (
                    <div
                      key={cat}
                      className="cat-card"
                      style={accentVars(data.color)}
                      onClick={() => selectCategory(cat)}
                    >
                      <span
                        className="cat-badge"
                        style={
                          adminOnly
                            ? {
                                color: 'var(--amber)',
                                backgroundColor: 'rgba(245,158,11,0.12)',
                                border: '1px solid rgba(245,158,11,0.4)',
                              }
                            : undefined
                        }
                      >
                        {adminOnly ? '★ ADMIN' : '● ACTIVE'}
                      </span>
                      <div className="cat-icon">
                        <SvgIcon paths={ICON_PATHS[data.icon]} color={data.color} />
                      </div>
                      <div className="cat-title">{cat}</div>
                      <div className="cat-desc">{data.description}</div>
                      <div className="cat-foot">
                        {data.footer} <span className="arrow">→</span>
                      </div>
                    </div>
                  );
                })}
                {user ? (
                  <>
                    <AIChatCard />
                    <MyManualsCard />
                  </>
                ) : (
                  <>
                    <LockedCard
                      icon="💬"
                      title="General Maritime Chat"
                      description="Chat about your own equipment manuals, or ask general maritime questions. Free trial included."
                    />
                    <LockedCard
                      icon="📚"
                      title="My Manuals"
                      description="Upload your equipment PDF manuals and get answers grounded in your own documentation."
                    />
                  </>
                )}
              </div>
              <PromptBar onSirePrep={() => selectCategory('Inspection')} />
            </>
          )}

          {category && !manufacturer && (
            <>
              <div className="section-label">SELECT MANUFACTURER</div>
              <div className="model-grid">
                {Object.keys(categoryData.manufacturers).map(mfr => (
                  <div
                    key={mfr}
                    className="select-card"
                    style={accentStyle}
                    onClick={() => selectManufacturer(mfr)}
                  >
                    <div className="sc-title">{mfr}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {category && manufacturer && !engineType && (
            <>
              <div className="section-label">SELECT TYPE</div>
              <div className="model-grid">
                {Object.keys(manufacturerData.types).map(type => (
                  <div
                    key={type}
                    className="select-card"
                    style={accentStyle}
                    onClick={() => selectEngineType(type)}
                  >
                    <div className="sc-title">{type}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {category && manufacturer && engineType && !selectedModel && (
            <>
              <div className="section-label">SELECT MODEL</div>
              <div className="model-grid">
                {Object.entries(engineTypeData.models).map(([modelName, modelData]) => (
                  <div
                    key={modelName}
                    className="select-card"
                    style={accentStyle}
                    onClick={() => selectModel(modelName, modelData)}
                  >
                    <div className="sc-title">{modelName}</div>
                    <div className="sc-sub">
                      {manufacturer} · {engineType}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* EQUIPMENT CONTEXT BAR */}
          {selectedModel && (
            <div className="equip-bar no-print" style={accentStyle}>
              <div className="equip-icon">
                <SvgIcon paths={ICON_PATHS[categoryData.icon]} size={20} color={accentColor} />
              </div>
              <div>
                <div className="equip-name">{selectedModel.name}</div>
                <div className="equip-path">
                  {category.toUpperCase()} / {manufacturer.toUpperCase()} /{' '}
                  {engineType.toUpperCase()}
                </div>
              </div>
              <button className="equip-change" onClick={() => selectEngineType(engineType)}>
                Change equipment
              </button>
            </div>
          )}

          {/* MOCK INSPECTION */}
          {selectedModel && selectedModel.mode === 'mock' && (
            <div style={accentStyle}>
              {!mockRank && !mockLoadingQ && (
                <>
                  <div className="section-label">SELECT YOUR RANK</div>
                  <div className="rank-grid">
                    {RANKS.map(rank => (
                      <div
                        key={rank}
                        className="select-card"
                        style={{ ...accentVars(G), padding: 16 }}
                        onClick={() => startMockInspection(rank)}
                      >
                        <div className="sc-title" style={{ fontSize: 13 }}>
                          {rank}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {mockLoadingQ && (
                <div style={{ textAlign: 'center', padding: 40, color: G }}>
                  Loading questions for {mockRank}...
                </div>
              )}

              {mockRank && !mockLoadingQ && mockQuestions.length === 0 && (
                <div className="panel" style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>📋</div>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
                    No inspection available
                  </div>
                  <p
                    style={{
                      fontSize: 13.5,
                      color: 'var(--text-2)',
                      lineHeight: 1.6,
                      margin: '0 auto 24px',
                      maxWidth: 420,
                    }}
                  >
                    {mockError || `No SIRE 2.0 questions are available for "${mockRank}" yet.`}
                  </p>
                  <button
                    className="btn"
                    onClick={() => startMockInspection(mockRank)}
                    style={{
                      background: 'var(--primary)',
                      color: '#ffffff',
                      padding: '12px 28px',
                    }}
                  >
                    ↻ Try again
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setMockRank(null);
                      setMockError(null);
                    }}
                    style={{ marginLeft: 12, padding: '12px 28px' }}
                  >
                    Choose another rank
                  </button>
                </div>
              )}

              {mockRank &&
                !mockLoadingQ &&
                mockQuestions.length > 0 &&
                mockIndex < mockQuestions.length && (
                  <div>
                    <div className="mock-score">
                      <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                        Question{' '}
                        <strong style={{ color: 'var(--text)' }}>
                          {mockIndex + 1} of {mockQuestions.length}
                        </strong>{' '}
                        · {mockRank}
                      </span>
                      <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span className="score-pill sp-pass">✓ {mockScore.pass} PASS</span>
                        <span className="score-pill sp-part">~ {mockScore.partial} PARTIAL</span>
                        <span className="score-pill sp-fail">✗ {mockScore.fail} FAIL</span>
                        <span className="score-pill sp-skip">— {mockScore.skipped} SKIPPED</span>
                      </span>
                    </div>
                    <div className="progress-track">
                      <div
                        className="progress-fill"
                        style={{ width: `${(mockIndex / mockQuestions.length) * 100}%` }}
                      />
                    </div>

                    <div className="panel" style={{ borderLeft: `3px solid ${G}` }}>
                      <div
                        style={{
                          fontFamily: 'var(--mono)',
                          fontSize: 11,
                          color: G,
                          letterSpacing: 1,
                          marginBottom: 8,
                        }}
                      >
                        SIRE 2.0 — {mockQuestions[mockIndex].question_number}
                      </div>
                      <div
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          lineHeight: 1.6,
                          marginBottom: 14,
                        }}
                      >
                        {mockQuestions[mockIndex].full_text}
                      </div>

                      <button
                        className="chip"
                        onClick={() => setShowExpects(!showExpects)}
                        style={{ ...accentVars(G), color: G }}
                      >
                        💡{' '}
                        {showExpects
                          ? 'Hide inspector expectations'
                          : 'What does the inspector expect?'}
                      </button>

                      {showExpects && (
                        <div
                          style={{
                            marginTop: 14,
                            padding: 16,
                            backgroundColor: 'rgba(52,211,153,0.06)',
                            borderRadius: 10,
                            border: '1px solid rgba(52,211,153,0.25)',
                          }}
                        >
                          {mockQuestions[mockIndex].objective && (
                            <div style={{ marginBottom: 10 }}>
                              <div
                                style={{
                                  color: G,
                                  fontSize: 10,
                                  letterSpacing: 1,
                                  marginBottom: 4,
                                  fontFamily: 'var(--mono)',
                                }}
                              >
                                OBJECTIVE
                              </div>
                              <div
                                style={{
                                  fontSize: 12.5,
                                  color: 'var(--text-2)',
                                  lineHeight: 1.55,
                                }}
                              >
                                {mockQuestions[mockIndex].objective}
                              </div>
                            </div>
                          )}
                          {mockQuestions[mockIndex].expected_evidence && (
                            <div style={{ marginBottom: 10 }}>
                              <div
                                style={{
                                  color: G,
                                  fontSize: 10,
                                  letterSpacing: 1,
                                  marginBottom: 4,
                                  fontFamily: 'var(--mono)',
                                }}
                              >
                                EXPECTED EVIDENCE
                              </div>
                              <div
                                style={{
                                  fontSize: 12.5,
                                  color: 'var(--text-2)',
                                  lineHeight: 1.55,
                                }}
                              >
                                {mockQuestions[mockIndex].expected_evidence}
                              </div>
                            </div>
                          )}
                          {mockQuestions[mockIndex].negative_grounds && (
                            <div>
                              <div
                                style={{
                                  color: 'var(--red)',
                                  fontSize: 10,
                                  letterSpacing: 1,
                                  marginBottom: 4,
                                  fontFamily: 'var(--mono)',
                                }}
                              >
                                POTENTIAL NEGATIVE GROUNDS
                              </div>
                              <div
                                style={{
                                  fontSize: 12.5,
                                  color: 'var(--text-2)',
                                  lineHeight: 1.55,
                                }}
                              >
                                {mockQuestions[mockIndex].negative_grounds}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {!inspectorStarted && (
                      <button
                        className="btn"
                        onClick={startInspectorChat}
                        style={{
                          width: '100%',
                          background: 'var(--primary)',
                          color: '#ffffff',
                          boxShadow: '0 4px 16px -4px rgba(52,211,153,0.4)',
                        }}
                      >
                        🎭 START INSPECTION →
                      </button>
                    )}

                    {inspectorStarted && (
                      <div className="panel">
                        <div className="panel-label" style={{ color: G }}>
                          <span className="panel-tick" style={{ background: G }} /> INSPECTION IN
                          PROGRESS
                        </div>

                        <div style={{ maxHeight: 450, overflowY: 'auto', marginBottom: 8 }}>
                          {inspectorMessages.map((msg, i) => (
                            <div
                              key={i}
                              className={`msg ${msg.role === 'inspector' ? 'insp' : 'user'}`}
                            >
                              <div
                                className={`avatar ${msg.role === 'inspector' ? 'insp' : 'you'}`}
                              >
                                {msg.role === 'inspector' ? '🕵️' : '👷'}
                              </div>
                              <div className="bubble">
                                <div className="who">
                                  {msg.role === 'inspector' ? 'INSPECTOR' : 'YOU'}
                                </div>
                                <div className="body">{msg.content}</div>
                                {msg.followups && msg.followups.length > 0 && (
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 8,
                                      flexWrap: 'wrap',
                                      marginTop: 8,
                                    }}
                                  >
                                    {msg.followups.map((fq, fi) => (
                                      <button
                                        key={fi}
                                        className="chip"
                                        style={accentVars(G)}
                                        onClick={() => setInspectorInput(fq)}
                                      >
                                        {fq}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {inspectorLoading && (
                            <div style={{ color: 'var(--text-3)', fontSize: 12, paddingLeft: 46 }}>
                              Inspector is typing...
                            </div>
                          )}
                          {inspectorError && !inspectorLoading && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                                paddingLeft: 46,
                                marginTop: 4,
                              }}
                            >
                              <span style={{ color: 'var(--red)', fontSize: 12.5 }}>
                                {inspectorError}
                              </span>
                              <button
                                className="btn btn-ghost"
                                onClick={retryInspector}
                                style={{ padding: '6px 14px', fontSize: 11.5, minHeight: 32 }}
                              >
                                ↻ Retry
                              </button>
                            </div>
                          )}
                        </div>

                        {inspectorVerdict && (
                          <div
                            style={{
                              backgroundColor:
                                inspectorVerdict.verdict === 'PASS'
                                  ? 'rgba(52,211,153,0.08)'
                                  : inspectorVerdict.verdict === 'FAIL'
                                    ? 'rgba(248,113,113,0.08)'
                                    : 'rgba(251,191,36,0.08)',
                              border: `1px solid ${inspectorVerdict.verdict === 'PASS' ? 'rgba(52,211,153,0.4)' : inspectorVerdict.verdict === 'FAIL' ? 'rgba(248,113,113,0.4)' : 'rgba(251,191,36,0.4)'}`,
                              borderRadius: 12,
                              padding: 20,
                              marginBottom: 16,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                marginBottom: 8,
                              }}
                            >
                              <span style={{ fontSize: 24 }}>
                                {inspectorVerdict.verdict === 'PASS'
                                  ? '✅'
                                  : inspectorVerdict.verdict === 'FAIL'
                                    ? '❌'
                                    : '⚠️'}
                              </span>
                              <span
                                style={{
                                  fontWeight: 800,
                                  fontSize: 18,
                                  color:
                                    inspectorVerdict.verdict === 'PASS'
                                      ? G
                                      : inspectorVerdict.verdict === 'FAIL'
                                        ? 'var(--red)'
                                        : 'var(--yellow)',
                                }}
                              >
                                {inspectorVerdict.verdict}
                              </span>
                            </div>
                            {inspectorVerdict.reason && (
                              <p
                                style={{
                                  fontSize: 13,
                                  margin: '0 0 16px 0',
                                  color: 'var(--text-2)',
                                  lineHeight: 1.6,
                                }}
                              >
                                {inspectorVerdict.reason}
                              </p>
                            )}
                            <button
                              className="btn"
                              onClick={nextMockQuestion}
                              style={{
                                background: 'var(--primary)',
                                color: '#ffffff',
                              }}
                            >
                              NEXT QUESTION →
                            </button>
                          </div>
                        )}

                        {!inspectorVerdict && (
                          <div className="inspector-input-row" style={accentVars(G)}>
                            <input
                              className="text-input"
                              style={{ flex: 1 }}
                              value={inspectorInput}
                              onChange={e => setInspectorInput(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') sendInspectorReply();
                              }}
                              placeholder="Type your response to the inspector..."
                            />
                            <button
                              className="btn"
                              onClick={sendInspectorReply}
                              disabled={inspectorLoading || !inspectorInput.trim()}
                              style={{
                                background: 'var(--primary)',
                                color: '#ffffff',
                              }}
                            >
                              Send →
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Secondary escape hatch: costs nothing, counts as
                          SKIPPED, never as a verdict. Hidden once a verdict
                          is in (NEXT QUESTION takes over). */}
                    {!inspectorVerdict && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                        <button
                          className="btn btn-ghost"
                          onClick={skipMockQuestion}
                          aria-label="Skip this question"
                          style={{ padding: '10px 22px', fontSize: 12, minHeight: 44 }}
                        >
                          Skip →
                        </button>
                      </div>
                    )}
                  </div>
                )}

              {mockRank &&
                !mockLoadingQ &&
                mockIndex >= mockQuestions.length &&
                mockQuestions.length > 0 && (
                  <div className="panel" style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 16 }}>🎯</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 20 }}>
                      Mock Inspection Complete!
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 40,
                        marginBottom: 28,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 40, fontWeight: 800, color: G }}>
                          {mockScore.pass}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text-3)',
                            fontFamily: 'var(--mono)',
                          }}
                        >
                          PASS
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--yellow)' }}>
                          {mockScore.partial}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text-3)',
                            fontFamily: 'var(--mono)',
                          }}
                        >
                          PARTIAL
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 40, fontWeight: 800, color: 'var(--red)' }}>
                          {mockScore.fail}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text-3)',
                            fontFamily: 'var(--mono)',
                          }}
                        >
                          FAIL
                        </div>
                      </div>
                    </div>
                    {mockScore.skipped > 0 && (
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--text-3)',
                          marginTop: -8,
                          marginBottom: 24,
                        }}
                      >
                        Skipped: {mockScore.skipped} — questions you chose to pass
                      </div>
                    )}
                    <button
                      className="btn"
                      onClick={() => {
                        setMockRank(null);
                        setMockQuestions([]);
                        setMockIndex(0);
                        setMockScore({ pass: 0, partial: 0, fail: 0, skipped: 0 });
                      }}
                      style={{
                        background: 'var(--primary)',
                        color: '#ffffff',
                        padding: '14px 36px',
                      }}
                    >
                      START AGAIN →
                    </button>
                  </div>
                )}
            </div>
          )}

          {/* CHECKLIST MODE */}
          {selectedModel && selectedModel.mode === 'checklist' && (
            <div>
              {/* Step 1: Select Department */}
              {!checklistDept && (
                <>
                  <div className="section-label">SELECT DEPARTMENT</div>
                  <div className="dept-grid">
                    {[
                      { id: 'DECK', icon: 'compass', label: 'DECK' },
                      { id: 'ENGINE', icon: 'gear', label: 'ENGINE' },
                    ].map(d => (
                      <div
                        key={d.id}
                        className="select-card"
                        style={{ ...accentVars(G), padding: 32, textAlign: 'center' }}
                        onClick={() => {
                          setChecklistDept(d.id);
                          setChecklistType('ALL');
                          loadChecklist(d.id, 'ALL');
                        }}
                      >
                        <div
                          style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}
                        >
                          <SvgIcon paths={ICON_PATHS[d.icon]} size={32} color={G} />
                        </div>
                        <div className="sc-title">{d.label}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Step 2: Filter + Results */}
              {checklistDept && (
                <div>
                  {/* Header */}
                  <div
                    className="no-print"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 24,
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '7px 14px', fontSize: 11 }}
                        onClick={() => {
                          setChecklistDept(null);
                          setChecklistData(null);
                        }}
                      >
                        ← BACK
                      </button>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 800 }}>
                          Pre-Inspection Checklist
                        </div>
                        <div
                          style={{
                            fontFamily: 'var(--mono)',
                            fontSize: 10.5,
                            color: 'var(--text-3)',
                            letterSpacing: 1.5,
                            marginTop: 2,
                          }}
                        >
                          {checklistDept} DEPARTMENT · SIRE 2.0
                        </div>
                      </div>
                    </div>
                    {checklistData && Object.keys(checklistData).length > 0 && (
                      <button
                        className="btn"
                        onClick={printChecklist}
                        style={{
                          background: 'var(--primary)',
                          color: '#ffffff',
                          padding: '10px 18px',
                          fontSize: 12,
                        }}
                      >
                        🖨 Print / Save PDF
                      </button>
                    )}
                  </div>

                  {/* Print-only header */}
                  <div className="print-only print-header">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700 }}>
                          {BRAND.logo} {BRAND.name.toUpperCase()}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4 }}>
                          Pre-Inspection Checklist — {checklistDept} DEPARTMENT
                        </div>
                        {checklistType !== 'ALL' && (
                          <div style={{ fontSize: 12, marginTop: 2 }}>Filter: {checklistType}</div>
                        )}
                      </div>
                      <div style={{ fontSize: 11, textAlign: 'right' }}>
                        <div>Date: {new Date().toLocaleDateString('en-GB')}</div>
                        <div style={{ marginTop: 4 }}>Vessel: _______________</div>
                        <div style={{ marginTop: 4 }}>Inspector: _______________</div>
                      </div>
                    </div>
                  </div>

                  {/* Filter buttons */}
                  <div className="checklist-filter no-print">
                    {['ALL', 'Hardware', 'Human', 'Process', 'Other'].map(type => (
                      <button
                        key={type}
                        className={`filter-btn ${checklistType === type ? 'active' : ''}`}
                        onClick={() => {
                          setChecklistType(type);
                          loadChecklist(checklistDept, type);
                        }}
                      >
                        {type === 'ALL' ? 'All' : type}
                      </button>
                    ))}
                  </div>

                  {checklistLoading && (
                    <div style={{ textAlign: 'center', padding: 40, color: G }}>
                      Loading checklist...
                    </div>
                  )}

                  {/* Results */}
                  {checklistData && !checklistLoading && (
                    <div>
                      {Object.keys(checklistData).length === 0 && (
                        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>
                          No observations found.
                        </div>
                      )}
                      {Object.entries(checklistData).map(([type, items]) => {
                        const typeColors = {
                          Hardware: {
                            bg: 'rgba(96,165,250,0.12)',
                            border: 'rgba(96,165,250,0.35)',
                            text: '#93c5fd',
                          },
                          Human: {
                            bg: 'rgba(251,191,36,0.12)',
                            border: 'rgba(251,191,36,0.35)',
                            text: '#fcd34d',
                          },
                          Process: {
                            bg: 'rgba(168,85,247,0.12)',
                            border: 'rgba(168,85,247,0.35)',
                            text: '#d8b4fe',
                          },
                          Other: {
                            bg: 'rgba(148,163,184,0.12)',
                            border: 'rgba(148,163,184,0.35)',
                            text: '#cbd5e1',
                          },
                        };
                        const colors = typeColors[type] || typeColors.Other;
                        return (
                          <div key={type} style={{ marginBottom: 24 }} className="print-section">
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                margin: '26px 0 12px',
                              }}
                            >
                              <span
                                className="cl-type print-type-badge"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                  border: `1px solid ${colors.border}`,
                                }}
                              >
                                {type.toUpperCase()}
                              </span>
                              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                                {items.length} observations
                              </span>
                            </div>
                            <div>
                              {items.map((item, i) => {
                                const key = `${type}-${i}`;
                                return (
                                  <div
                                    key={i}
                                    className={`cl-item print-item ${checkedItems[key] ? 'checked' : ''}`}
                                    onClick={() =>
                                      setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }))
                                    }
                                  >
                                    <div className="cl-check print-checkbox" />
                                    <div style={{ flex: 1 }}>
                                      <div className="cl-qn print-qnum">
                                        {item.question_number}
                                        {item.question_text
                                          ? ` · ${item.question_text.slice(0, 60)}...`
                                          : ''}
                                      </div>
                                      <div className="cl-text print-obs-text">
                                        {item.observation_text}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {/* Print footer */}
                      <div className="print-only print-footer">
                        AI-assisted reference only. Always verify against your company's official
                        documentation and SMS. Generated by {BRAND.name}.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Q&A MODE */}
          {selectedModel && selectedModel.mode !== 'mock' && selectedModel.mode !== 'checklist' && (
            <div style={accentStyle}>
              {chatHistory.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  {chatHistory.map((h, i) => (
                    <div key={i} style={{ marginBottom: 12, opacity: 0.6 }}>
                      <div
                        style={{
                          backgroundColor: 'var(--surface-1)',
                          border: '1px solid var(--border-soft)',
                          borderRadius: 10,
                          padding: 12,
                          fontSize: 12.5,
                          color: accentColor,
                          marginBottom: 4,
                        }}
                      >
                        Q: {h.question}
                      </div>
                      {h.result?.assessment && (
                        <div
                          style={{
                            backgroundColor: 'var(--surface-1)',
                            border: '1px solid var(--border-soft)',
                            borderRadius: 10,
                            padding: 12,
                            fontSize: 12.5,
                            color: 'var(--text-2)',
                          }}
                        >
                          {h.result.assessment}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* INPUT AREA — hidden when waiting for clarify option selection */}
              {(!result || result.response_type !== 'clarify') && (
                <div className="panel">
                  <div className="panel-label">
                    <span className="panel-tick" style={{ background: accentColor }} />
                    {chatHistory.length > 0
                      ? 'FOLLOW-UP QUESTION'
                      : 'DESCRIBE THE PROBLEM OR ALARM'}
                  </div>
                  <textarea
                    className="input-field"
                    value={problem}
                    onChange={e => setProblem(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && e.ctrlKey) analyze();
                    }}
                    placeholder={selectedModel.placeholder}
                  />
                  <div className="example-tags">
                    {selectedModel.examples.map((ex, i) => (
                      <button key={i} className="chip" onClick={() => setProblem(ex)}>
                        {ex}
                      </button>
                    ))}
                  </div>
                  <button
                    className="btn"
                    onClick={analyze}
                    disabled={loading || !problem.trim()}
                    style={{
                      marginTop: 18,
                      background: `var(--primary)`,
                      color: '#ffffff',
                      boxShadow: `0 4px 16px -4px ${accentColor}66`,
                    }}
                  >
                    {loading
                      ? 'Analyzing...'
                      : chatHistory.length > 0
                        ? 'Follow-up →'
                        : 'Analyze problem →'}
                  </button>
                </div>
              )}
              {error && (
                <div style={{ color: 'var(--red)', fontSize: 12.5, marginBottom: 16 }}>{error}</div>
              )}
              {result && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {result.assessment && (
                    <div className="panel panel-assessment">
                      <div className="panel-label" style={{ color: accentColor }}>
                        <span className="panel-tick" style={{ background: accentColor }} />{' '}
                        ASSESSMENT
                      </div>
                      <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.7 }}>
                        {result.assessment}
                      </p>
                    </div>
                  )}

                  {/* CLARIFY — option buttons for guided diagnosis */}
                  {result.response_type === 'clarify' && (
                    <>
                      {result.immediate_actions?.length > 0 && (
                        <div
                          className="panel"
                          style={{
                            backgroundColor: 'rgba(248,113,113,0.06)',
                            background: 'rgba(248,113,113,0.06)',
                            border: '1px solid rgba(248,113,113,0.35)',
                          }}
                        >
                          <div className="panel-label" style={{ color: 'var(--red)' }}>
                            <span className="panel-tick" style={{ background: 'var(--red)' }} /> ⚠
                            IMMEDIATE ACTIONS
                          </div>
                          {result.immediate_actions.map((a, i) => (
                            <div key={i} className="check-item">
                              <span className="mark" style={{ color: 'var(--red)' }}>
                                ▸
                              </span>
                              <span>{a}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="panel" style={{ border: `1px solid ${accentColor}66` }}>
                        <div className="panel-label" style={{ color: accentColor }}>
                          <span className="panel-tick" style={{ background: accentColor }} />{' '}
                          DIAGNOSTIC QUESTION
                        </div>
                        <p
                          style={{
                            margin: '0 0 20px 0',
                            fontSize: 14.5,
                            lineHeight: 1.6,
                            fontWeight: 600,
                          }}
                        >
                          {result.question}
                        </p>

                        {/* Option buttons */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                            marginBottom: 16,
                          }}
                        >
                          {result.options?.map((opt, i) => (
                            <button
                              key={i}
                              onClick={() => analyzeWithAnswer(opt.label)}
                              disabled={loading}
                              style={{
                                backgroundColor: 'transparent',
                                border: `1px solid ${accentColor}55`,
                                borderRadius: 10,
                                padding: '13px 16px',
                                color: 'var(--text)',
                                fontSize: 13.5,
                                fontFamily: 'var(--sans)',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                textAlign: 'left',
                                transition: 'border-color 0.15s, background 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.borderColor = accentColor;
                                e.currentTarget.style.backgroundColor = `${accentColor}11`;
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.borderColor = `${accentColor}55`;
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <span
                                style={{
                                  color: accentColor,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  flexShrink: 0,
                                  fontFamily: 'var(--mono)',
                                }}
                              >
                                {String.fromCharCode(65 + i)}
                              </span>
                              <span>{opt.label}</span>
                            </button>
                          ))}

                          {/* Other — custom input */}
                          {result.allow_custom !== false &&
                            (!showCustomInput ? (
                              <button
                                onClick={() => setShowCustomInput(true)}
                                disabled={loading}
                                style={{
                                  backgroundColor: 'transparent',
                                  border: '1px solid var(--border)',
                                  borderRadius: 10,
                                  padding: '13px 16px',
                                  color: 'var(--text-3)',
                                  fontSize: 13.5,
                                  fontFamily: 'var(--sans)',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                }}
                              >
                                <span style={{ fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                                  ✏
                                </span>
                                <span>Other — type your answer</span>
                              </button>
                            ) : (
                              <div
                                style={{
                                  border: '1px solid var(--border)',
                                  borderRadius: 10,
                                  padding: 14,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 10,
                                }}
                              >
                                <textarea
                                  autoFocus
                                  className="input-field"
                                  style={{
                                    minHeight: 72,
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                  }}
                                  value={customAnswer}
                                  onChange={e => setCustomAnswer(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && e.ctrlKey && customAnswer.trim())
                                      analyzeWithAnswer(customAnswer);
                                  }}
                                  placeholder="Describe your specific situation..."
                                />
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button
                                    className="btn"
                                    onClick={() => {
                                      if (customAnswer.trim()) analyzeWithAnswer(customAnswer);
                                    }}
                                    disabled={loading || !customAnswer.trim()}
                                    style={{
                                      background: `var(--primary)`,
                                      color: '#ffffff',
                                      padding: '9px 18px',
                                      fontSize: 12,
                                    }}
                                  >
                                    {loading ? 'Analyzing...' : 'Send →'}
                                  </button>
                                  <button
                                    className="btn btn-ghost"
                                    style={{ padding: '9px 14px', fontSize: 12 }}
                                    onClick={() => {
                                      setShowCustomInput(false);
                                      setCustomAnswer('');
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ))}
                        </div>
                        {loading && (
                          <div style={{ color: 'var(--text-3)', fontSize: 11, marginTop: 8 }}>
                            Analyzing your answer...
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {/* TROUBLESHOOTING FORMAT */}
                  {result.response_type !== 'general' && result.response_type !== 'clarify' && (
                    <>
                      {result.causes?.length > 0 && (
                        <div className="panel">
                          <div className="panel-label">
                            <span className="panel-tick" style={{ background: 'var(--red)' }} />{' '}
                            POSSIBLE CAUSES
                          </div>
                          {result.causes.map((c, i) => (
                            <div key={i} className="sev-row">
                              <span
                                className="sev-chip"
                                style={SEV_STYLES[c.severity] || SEV_STYLES.LOW}
                              >
                                {c.severity}
                              </span>
                              <span style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                                {c.description}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {result.checks?.length > 0 && (
                        <div className="panel">
                          <div className="panel-label">
                            <span className="panel-tick" style={{ background: 'var(--cyan)' }} />{' '}
                            IMMEDIATE CHECKS
                          </div>
                          {result.checks.map((c, i) => (
                            <div key={i} className="check-item">
                              <span className="mark">▸</span>
                              <span>{c}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {result.steps?.length > 0 && (
                        <div className="panel">
                          <div className="panel-label">
                            <span className="panel-tick" style={{ background: 'var(--green)' }} />{' '}
                            TROUBLESHOOTING PROCEDURE
                          </div>
                          <div className="timeline">
                            {result.steps.map((s, i) => (
                              <div key={i} className="t-step">
                                <div className="t-num">{s.number || i + 1}</div>
                                <div>
                                  <div className="t-title">{s.title}</div>
                                  <div className="t-desc">{s.description}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* GENERAL Q&A FORMAT */}
                  {result.response_type === 'general' && (
                    <>
                      {result.explanation && (
                        <div className="panel">
                          <div className="panel-label">
                            <span className="panel-tick" style={{ background: accentColor }} />{' '}
                            EXPLANATION
                          </div>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 13.5,
                              lineHeight: 1.8,
                              color: 'var(--text)',
                            }}
                          >
                            {result.explanation}
                          </p>
                        </div>
                      )}
                      {result.key_points?.length > 0 && (
                        <div className="panel">
                          <div className="panel-label">
                            <span className="panel-tick" style={{ background: accentColor }} /> KEY
                            POINTS
                          </div>
                          {result.key_points.map((kp, i) => (
                            <div key={i} className="check-item">
                              <span className="mark">▸</span>
                              <span>{kp}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {/* SOURCES USED — admin only, never shown to end users.
                          Gated by the server-verified is_admin flag (the
                          legacy password-panel gate is gone). */}
                  {isAdmin && sources.length > 0 && (
                    <SourcesPanel sources={sources} accentColor={accentColor} />
                  )}

                  <div className="disclaimer">
                    ⚠ AI-assisted reference only. Always verify against your company's official
                    documentation and SMS.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <footer className="no-print site-footer">
          {BRAND.logo} {BRAND.name} — {BRAND.tagline}
          <br />
          {BRAND.note}
          <br />
          <a href="#terms" style={{ color: 'var(--text-3)' }}>
            Terms
          </a>
          {' · '}
          <a href="#privacy" style={{ color: 'var(--text-3)' }}>
            Privacy
          </a>
        </footer>
      </div>
    </div>
  );
}
